/** Proveedor de identidad y tenant activo (componente solo; el objeto vive en AuthContext). */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import type { UserInfo } from '@/types';
import { authApi } from '@/services/api/authApi';
import { useProfile } from '@/services/api/useAuth';
import { useIdleTimeout } from '@/hooks/useIdleTimeout';
import { AuthContext } from './AuthContext';

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<UserInfo | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const { data, isPending, isError } = useProfile();

  useEffect(() => {
    if (isInitialized) return;
    if (data) {
      setUser(data);
      setIsInitialized(true);
    } else if (isError) {
      setUser(null);
      setIsInitialized(true);
    }
  }, [data, isError, isInitialized]);

  const expireSession = useCallback(() => {
    void authApi.logout().finally(() => {
      queryClient.clear();
      setUser(null);
      window.location.href = '/login';
    });
  }, [queryClient]);

  const handleIdle = useCallback(() => {
    if (!user) return;
    toast.error('Sesión expirada por inactividad');
    expireSession();
  }, [user, expireSession]);

  const handleAbsolute = useCallback(() => {
    if (!user) return;
    toast.error('Sesión expirada por tiempo máximo');
    expireSession();
  }, [user, expireSession]);

  useIdleTimeout({
    idleMs: 30 * 60 * 1000,
    absoluteMs: 8 * 60 * 60 * 1000,
    onIdle: handleIdle,
    onAbsolute: handleAbsolute,
    enabled: !!user,
  });

  const hasPermission = useCallback(
    (code: string) => !!user?.permissions.includes(code),
    [user],
  );

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated: !!user,
      isLoading: !isInitialized && isPending,
      isSuperuser: user?.permissions.includes('platform.tenants.operate') ?? false,
      hasPermission,
      setUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}
